import { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { GripVertical } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * @typedef {Object} DraggableItemProps
 * @property {React.ReactNode} children - The content of the draggable item.
 * @property {string} id - The unique identifier for the item.
 * @property {number} index - The index of the item in the list.
 * @property {(dragIndex: number, hoverIndex: number) => void} moveItem - Function to move the item in the list.
 * @property {string} dndGroup - The drag-and-drop group type.
 */

/**
 * A component that represents a draggable item in a list.
 * It uses react-dnd for drag-and-drop functionality.
 * @param {DraggableItemProps} props - The properties for the DraggableItem component.
 */
export default function DraggableItem({ children, id, index, moveItem, dndGroup }) {
  const ref = useRef(null);

  // Set up drag
  const [{ isDragging }, drag] = useDrag({
    type: dndGroup,
    item: { id, index },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // Set up drop
  const [, drop] = useDrop({
    accept: dndGroup,
    hover: item => {
      if (!ref.current) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Move the item
      moveItem(dragIndex, hoverIndex);

      // Update the index for the dragged item
      item.index = hoverIndex;
    },
  });

  // Connect the drag and drop refs
  drag(drop(ref));

  return (
    <div
      ref={ref}
      className={cn(
        'flex items-center p-3 bg-white border rounded-md shadow-sm mb-2 transition-all duration-200',
        {
          'opacity-50 scale-98 transition-all duration-200 border-blue-500 border-2 shadow-md':
            isDragging,
        },
      )}>
      <div className="mr-3 text-gray-400 hover:text-gray-600 cursor-move">
        <GripVertical className="w-5 h-5" />
      </div>
      {children}
    </div>
  );
}
